import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, BriefcaseIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { User } from '../../types';
import Avatar from '../common/Avatar';

interface CollaborationOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onViewServices: () => void;
  onCustomRequest: () => void;
  hasServices?: boolean;
}

const CollaborationOptionsModal: React.FC<CollaborationOptionsModalProps> = ({
  isOpen,
  onClose,
  user,
  onViewServices,
  onCustomRequest,
  hasServices,
}) => {
  if (!isOpen) return null;

  // Show View Services if either hasServices prop is true OR services_count > 0
  const showViewServices = hasServices || (user.services_count ?? 0) > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999998]"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden pointer-events-auto"
            >
              {/* Header */}
              <div className="relative p-6 text-center border-b border-gray-100 dark:border-gray-700">
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
                
                <div className="flex flex-col items-center">
                  <Avatar 
                    src={user.profile_pic_signed || user.profile_pic_thumb_signed || user.profile_pic} 
                    alt={user.first_name} 
                    size="lg" 
                    className="mb-3 border-4 border-white dark:border-gray-700 shadow-md" 
                  />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Collaborate with {user.first_name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Choose how you'd like to connect
                  </p>
                </div>
              </div>

              {/* Options */}
              <div className="p-6 space-y-4">
                {showViewServices && (
                <button
                  onClick={onViewServices}
                  className="w-full group flex items-center p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-black dark:hover:border-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 text-left"
                >
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg group-hover:bg-orange-100 dark:group-hover:bg-orange-900/50 transition-colors">
                    <BriefcaseIcon className="w-6 h-6" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-900 dark:text-white">View Services</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      Browse service packages and book directly
                    </p>
                  </div>
                </button>
                )}

                <button
                  onClick={onCustomRequest}
                  className="w-full group flex items-center p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-black dark:hover:border-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 text-left"
                >
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg group-hover:bg-orange-100 dark:group-hover:bg-orange-900/50 transition-colors">
                    <PaperAirplaneIcon className="w-6 h-6" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-900 dark:text-white">Custom Request</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      Send a personalized collaboration proposal
                    </p>
                  </div>
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CollaborationOptionsModal;
